import type { UIMessage } from 'ai';
import { createScopedLogger } from '~/utils/logger';
import type { StoreFileAction } from '~/types/actions';
import { isAssetFile } from '~/utils/fileIgnore';
import { setUniqueId, uniqueIdStore, userStore } from '~/lib/stores/user';
import { generateObfuscatedId } from '~/lib/utils/uniqueHash';
import type { ConnectionData, ProjectIdInterface } from '~/services/interfaceService';
import { needsMigrationStore } from '~/lib/stores/projectIdMigration';
import * as Sentry from '@sentry/remix';
import { chatStore } from '~/lib/stores/chat';
import type { ChatHistoryItem, DualiteEvent, Connections, ProjectType } from '~/services/interfaceService';
import { v4 as uuidv4 } from 'uuid';

// cloudRevert
// import {
//   setEventStatus,
//   eventStatusStore,
//   type EventErrorState,
//   type EventErrorDetails,
// } from '~/lib/stores/eventStatus';
// import { ensureEventStatusConnection, isConnected } from '~/lib/utils/eventStatusConnection';

// In-memory flag to track if migration is in progress (prevents duplicate runs)
let migrationInProgress = false;

const logger = createScopedLogger('ChatHistory');

// const emails = [
//   'shivam@dualite.dev',
//   'mukul@dualite.dev',
//   'prakhar@dualite.dev',
//   'shivgun03@gmail.com',
//   'rohan@dualite.dev',
// ];

// Compute SHA-256 hex digest for action content (supports string or Blob)
export async function computeContentHash(content: string | Blob): Promise<string> {
  try {
    const data =
      typeof content === 'string' ? new TextEncoder().encode(content) : new Uint8Array(await content.arrayBuffer());
    const digest = await crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(digest);
    let hex = '';

    for (let i = 0; i < bytes.length; i++) {
      const h = bytes[i].toString(16).padStart(2, '0');
      hex += h;
    }
    return hex;
  } catch (e) {
    console.error('[contentHash] Failed to compute content hash', e);
    return '';
  }
}

export async function withContentHash(action: StoreFileAction): Promise<StoreFileAction> {
  if (action.type !== 'file' || action.deleted) {
    return action;
  }

  if (action.content && !action.contentHash) {
    const contentHash = await computeContentHash(action.content);
    return { ...action, contentHash } as StoreFileAction;
  }

  return action;
}

// Function to convert base64 to Blob
export function base64ToBlob(base64: string, mimeType: string = 'application/octet-stream'): Blob {
  // Remove data URL prefix if present
  const base64Data = base64.includes('base64,') ? base64.split('base64,')[1] : base64;

  // Convert base64 to binary
  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);

    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
}

// Process file actions to convert asset files from base64 to blobs
export function processFileActions(fileActions: StoreFileAction[]): StoreFileAction[] {
  return fileActions.map((action) => {
    // Only process create and update actions that have content property
    if ('content' in action && action.type === 'file') {
      if (isAssetFile(action.filePath) && typeof action.content === 'string') {
        // Determine MIME type from file extension
        const extension = action.filePath.split('.').pop()?.toLowerCase() || '';
        const mimeMap: Record<string, string> = {
          png: 'image/png',
          jpg: 'image/jpeg',
          jpeg: 'image/jpeg',
          gif: 'image/gif',
          ico: 'image/x-icon',
          webp: 'image/webp',
          bmp: 'image/bmp',
          tiff: 'image/tiff',
        };
        const mimeType = mimeMap[extension] || 'application/octet-stream';

        // Convert base64 to blob
        return {
          ...action,
          content: base64ToBlob(action.content, mimeType),
          isBinary: true,
        };
      } else if (action.isBlob === true) {
        return {
          ...action,
          isBinary: true,
        };
      }
    }

    return action;
  });
}

// Check if boltHistory database exists
export async function checkBoltHistoryExists(): Promise<boolean> {
  return new Promise((resolve) => {
    const request = indexedDB.open('boltHistory');

    request.onsuccess = () => {
      const db = request.result;

      try {
        if (db?.objectStoreNames?.length === 0) {
          console.log('[checkBoltHistoryExists] No object stores found in boltHistory database');

          /*
           * alert(
           *   "Old IndexDB existed but empty. You're now using the latest version of Dualite Alpha. No need to worry, a routine upgrade was done. \n\n Enjoy your day!",
           * );
           */
          db.close();
          resolve(false);
        }
      } catch (error) {
        /*
         * Sentry.captureException(error, {
         *   extra: {
         *     message: 'Error in db migration',
         *   },
         * });
         */
        console.log('db migration edge case failed. contact support');
      }

      console.log('[checkBoltHistoryExists] db:', db);
      db.close();
      resolve(true);
    };

    request.onerror = () => {
      resolve(false);
    };
  });
}

// Check if dualiteHistory database exists
export async function checkDualiteHistoryExists(): Promise<boolean> {
  return new Promise((resolve) => {
    const request = indexedDB.open('dualiteHistory');

    request.onsuccess = () => {
      const db = request.result;

      try {
        if (db?.objectStoreNames?.length === 0) {
          console.log('[checkDualiteHistoryExists] No object stores found in dualiteHistory database');
          db.close();
          resolve(false);
          return;
        }
      } catch (error) {
        console.log('[checkDualiteHistoryExists] Error checking dualiteHistory database:', error);
        db.close();
        resolve(false);
        return;
      }

      console.log('[checkDualiteHistoryExists] dualiteHistory database exists with stores');
      db.close();
      resolve(true);
    };

    request.onerror = () => {
      console.log('[checkDualiteHistoryExists] dualiteHistory database does not exist');
      resolve(false);
    };
  });
}

// Open boltHistory database
export async function openBoltDatabase(): Promise<IDBDatabase | undefined> {
  return new Promise((resolve) => {
    const request = indexedDB.open('boltHistory', 1);

    request.onsuccess = (event: Event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event: Event) => {
      resolve(undefined);
      logger.error((event.target as IDBOpenDBRequest).error);
    };
  });
}

// Delete boltHistory database after migration
export async function deleteBoltHistory(): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase('boltHistory');

    request.onsuccess = () => {
      logger.info('boltHistory database deleted successfully');
      resolve();
    };

    request.onerror = () => {
      logger.error('Failed to delete boltHistory database');
      reject(new Error('Failed to delete boltHistory database'));
    };
  });
}

// this is used at the top level and never rejects
export async function openDatabase(): Promise<IDBDatabase | undefined> {
  return new Promise((resolve) => {
    const request = indexedDB.open('dualiteHistory', 4);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const transaction = (event.target as IDBOpenDBRequest).transaction!;
      const oldVersion = event.oldVersion;

      // Version 1: Create object store and initial indices
      if (oldVersion < 1) {
        if (!db.objectStoreNames.contains('chats')) {
          const store = db.createObjectStore('chats', { keyPath: 'id' });

          store.createIndex('id', 'id', { unique: true });
          store.createIndex('urlId', 'urlId', { unique: true });
        }
      }

      // Version 2: Add projectId index and field to existing records
      if (oldVersion < 2) {
        const store = transaction.objectStore('chats');

        // Create the projectId index if it doesn't exist
        if (!store.indexNames.contains('projectId')) {
          store.createIndex('projectId', 'projectId', { unique: true });
          console.log('[projectId] Created projectId index');
        }

        /*
         * Schema updated. Data migration will be triggered when user token becomes available
         * The migration function will check database state to determine if migration is needed
         */
        console.log('[projectId] Schema updated (will migrate data when user token is available)');
      }

      // Version 3: Create DualiteEvents object store for AI iteration tracking
      if (oldVersion < 3) {
        if (!db.objectStoreNames.contains('DualiteEvents')) {
          const eventsStore = db.createObjectStore('DualiteEvents', { keyPath: 'eventId' });
          eventsStore.createIndex('eventId', 'eventId', { unique: true });
          console.log('[DualiteEvents] Created DualiteEvents object store');
        }
      }

      // Version 4: Change primary key from 'id' to 'projectId'
      if (oldVersion < 4) {
        console.log('[Schema v4] Starting migration: changing primary key from id to projectId');

        // 1. Get reference to the existing store
        const oldStore = transaction.objectStore('chats');
        console.log('[Schema v4] oldStore created');

        // 2. RENAME the old store. This preserves data but frees up the 'chats' name.
        oldStore.name = 'chats_legacy_v3';
        console.log('[Schema v4] oldStore renamed to chats_legacy_v3');

        // 3. CREATE the new store immediately with the new KeyPath.
        const newStore = db.createObjectStore('chats', { keyPath: 'projectId' });
        console.log('[Schema v4] newStore created');
        newStore.createIndex('projectId', 'projectId', { unique: true });
        console.log('[Schema v4] newStore index created');

        // 4. Migrate data using a cursor
        const cursorRequest = oldStore.openCursor();

        cursorRequest.onsuccess = (event) => {
          console.log('[Schema v4] cursor opened');

          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          console.log('[Schema v4] cursor result >>>', cursor);

          if (cursor) {
            console.log('[Schema v4] cursor value >>>', cursor.value);

            const chat = cursor.value as ChatHistoryItem;
            console.log('[Schema v4] chat >>>', chat);

            if (!chat) {
              cursor.continue();
              return;
            }

            if (!chat.projectId) {
              console.log('[Schema v4] generating a new projectId');
              chat.projectId = uuidv4();
            }

            // Write to the NEW store
            const putRequest = newStore.put(chat);

            putRequest.onerror = (event) => {
              console.error('[Schema v4] error putting chat to newStore', event);
              Sentry.captureException(event, {
                extra: {
                  message: '[Schema v4] error putting chat to newStore',
                },
              });
              cursor.continue();
            };

            putRequest.onsuccess = () => {
              console.log('[Schema v4] chat put to newStore');
              cursor.continue();
            };
          } else {
            console.log('[Schema v4] Migration complete');

            // Validate migration - count records in both stores
            // const countOldRequest = oldStore.count();
            // const countNewRequest = newStore.count();

            // countOldRequest.onsuccess = () => {
            //   const oldCount = countOldRequest.result;
            //   console.log('[Schema v4] Old store record count:', oldCount);

            //   countNewRequest.onsuccess = () => {
            //     const newCount = countNewRequest.result;
            //     console.log('[Schema v4] New store record count:', newCount);

            //     if (oldCount === newCount) {
            //       console.log(`[Schema v4] ✅ Migration verified: ${newCount} records successfully migrated`);
            //     } else {
            //       console.error(
            //         `[Schema v4] ⚠️ Migration count mismatch: ${oldCount} old records -> ${newCount} new records`,
            //       );
            //     }
            //   };

            //   countNewRequest.onerror = () => {
            //     console.error('[Schema v4] Error counting new store records');
            //   };
            // };

            // countOldRequest.onerror = () => {
            //   console.error('[Schema v4] Error counting old store records');
            // };

            // NOTE: We cannot delete 'chats_legacy_v3' here because we are inside
            // an async callback. It must remain until a future version (v5)
            // cleans it up, or you can leave it as a backup.
          }
        };

        cursorRequest.onerror = (event) => {
          console.error('[Schema v4] error opening cursor', event);
          Sentry.captureException(event, {
            extra: {
              message: '[Schema v4] error opening cursor',
            },
          });
        };
      }
    };

    request.onsuccess = (event: Event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      Sentry.captureMessage('[Schema v4] IndexedDB migrated & opened successfully', {
        extra: {
          message: '[Schema v4] IndexedDB migrated & opened successfully',
        },
      });
      resolve(db);
    };

    request.onerror = (event: Event) => {
      // It is good practice to inspect the error here
      console.error('IndexedDB Error:', (event.target as IDBOpenDBRequest).error);
      Sentry.captureException(event.target as IDBOpenDBRequest, {
        extra: {
          message: '[Schema v4] IndexedDB error Migration & opening',
        },
      });
      resolve(undefined);
    };
  });
}

/**
 * Create or update an event in DualiteEvents objectStore
 */
export async function upsertEvent(db: IDBDatabase, event: Partial<DualiteEvent> & { eventId: string }): Promise<void> {
  // cloudRevert
  return Promise.resolve();

  // const currentUserEmail = userStore.get().email;

  // if (!currentUserEmail || !emails.includes(currentUserEmail)) {
  //   console.log('[pushEventToQueueIfComplete] Skipping enqueue: user email not allowed or missing:', currentUserEmail);
  //   return Promise.resolve();
  // }

  console.log('[upsertEvent] event >>>', event);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('DualiteEvents', 'readwrite');
    const store = transaction.objectStore('DualiteEvents');
    const getRequest = store.get(event.eventId);

    getRequest.onsuccess = () => {
      const existingEvent = getRequest.result as DualiteEvent | undefined;

      console.log('[DualiteEvents] existingEvent >>>', existingEvent);

      const updatedEvent: DualiteEvent = {
        ...existingEvent,
        ...event,
        eventId: event.eventId,
        enqueued: event.enqueued || false,
      } as DualiteEvent;

      if (!event.enqueued) {
        chatStore.setKey('enqueued', false);
      }

      if (Object.prototype.hasOwnProperty.call(event, 'error')) {
        if (event.error === null) {
          delete updatedEvent.error;
        } else if (event.error) {
          updatedEvent.error = event.error;
        }
      }

      console.log('[DualiteEvents] existingEvent updated >>>', updatedEvent);

      if (event.messages) {
        updatedEvent.messagesSet = true;
      }

      if (event.fileActions) {
        updatedEvent.fileActionsSet = true;
      }

      const putRequest = store.put(updatedEvent);

      putRequest.onsuccess = () => {
        console.log('[DualiteEvents] Event upserted:', event.eventId);
        resolve();
      };

      putRequest.onerror = () => {
        console.error('[DualiteEvents] Error upserting event:', putRequest.error);
        reject(putRequest.error);
      };
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
}

/**
 * Get an event by eventId
 */
async function getEventIDB(db: IDBDatabase, eventId: string): Promise<DualiteEvent | undefined> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('DualiteEvents', 'readonly');
    const store = transaction.objectStore('DualiteEvents');
    const request = store.get(eventId);

    request.onsuccess = () => resolve(request.result as DualiteEvent | undefined);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Check if file actions exist for a messageId in the workbench store
 */
// moved to workbench.ts; use dynamic import where needed to avoid eager client-only deps

/**
 * Push event to queue based on the completion criteria:
 * 1. If there are no file actions in workbench for this messageId AND fileActions are set in event AND projectId is set AND cloudChatId is set, push to queue
 * 2. If there are file actions in workbench AND messages AND fileActions AND projectId AND cloudChatId are all set , push to queue
 */
export async function pushEventToQueueIfComplete(
  db: IDBDatabase,
  eventId: string,
  changeSource: 'manual' | 'ai' = 'ai',
  isRetry: boolean = false,
): Promise<void> {
  // cloudRevert
  return Promise.resolve();

  // const event = await getEventIDB(db, eventId);

  // try {
  //   console.log('[pushEventToQueueIfComplete] Event found:', event);

  //   // Allow-enqueue filter based on user email.
  //   // Only users whose email is in the allowlist are permitted to enqueue events.
  //   // const currentUserEmail = userStore.get().email;

  //   // if ((!currentUserEmail || !emails.includes(currentUserEmail)) && !isRetry) {
  //   //   console.log(
  //   //     '[pushEventToQueueIfComplete] Skipping enqueue: user email not allowed or missing:',
  //   //     currentUserEmail,
  //   //   );
  //   //   return;
  //   // }

  //   if (!event) {
  //     console.warn('[DualiteEvents] Event not found:', eventId);
  //     return;
  //   }

  //   if (!event?.messagesSet && changeSource === 'ai' && !isRetry) {
  //     console.log('[DualiteEvents] Event is not complete, skipping queue:', eventId);
  //     return;
  //   }

  //   if (!event?.projectId) {
  //     console.log('[DualiteEvents] Event is not complete, skipping queue:', eventId);
  //     return;
  //   }

  //   if (!event?.cloudChatId) {
  //     console.log('[DualiteEvents] Event is not complete as cloudChatId is not set, skipping queue:', eventId);
  //     return;
  //   }

  //   // Check if file actions exist in workbench for this messageId (dynamic import to avoid bundling client-only deps)
  //   const { hasFileActionsInWorkbench } = await import('~/lib/stores/workbench');
  //   const hasFileActions = hasFileActionsInWorkbench(eventId);

  //   console.log('[DualiteEvents] Event check:', {
  //     eventId,
  //     messagesSet: event.messagesSet,
  //     fileActionsSet: event.fileActionsSet,
  //     projectId: event.projectId,
  //     hasFileActionsInWorkbench: hasFileActions,
  //     cloudChatId: event.cloudChatId,
  //   });

  //   // Case 1: File actions exist in workbench but fileActions not set in event
  //   if (hasFileActions && !event?.fileActionsSet && !isRetry) {
  //     console.log('[DualiteEvents] File actions exist in workbench but fileActions not set in event');
  //     return;
  //   }

  //   const cacheTime = new Date().toISOString();
  //   await updateProjectCacheTime(db, event.projectId as string, cacheTime);

  //   event.cacheTime = cacheTime;

  //   console.log('[updateProjectCacheTime] Event with updated cacheTime >>>', event);

  //   // Upload Blob-backed fileActions using multipart/form-data and strip content
  //   if (Array.isArray(event.fileActions) && event.fileActions.length > 0) {
  //     const blobFiles = event.fileActions.filter(
  //       (fa: any) => fa && fa.type === 'file' && fa.content && typeof fa.content !== 'string',
  //     );

  //     if (blobFiles.length > 0) {
  //       const form = new FormData();
  //       form.append('bucketName', 'files-dualite');
  //       form.append('projectId', String(event.projectId));
  //       form.append('eventId', String(event.eventId));

  //       for (const fa of blobFiles) {
  //         const blob: Blob = fa.content as Blob;
  //         const fileName = String(fa.contentHash || fa.filePath);
  //         const type = (blob as any).type || 'image/png';
  //         console.log('[pushEventToQueueIfComplete] type >>>', type);

  //         const file = new File([blob], fileName, { type });
  //         form.append('files', file);
  //       }

  //       const uploadRes = await fetch('/api/file-actions/upload', {
  //         method: 'POST',
  //         body: form,
  //       });

  //       if (!uploadRes.ok && uploadRes.status !== 207) {
  //         const err = await uploadRes.text();
  //         throw new Error(`[multipart upload fileActions] ${err}`);
  //       }

  //       // Remove content field for blob-backed entries
  //       event.fileActions = event.fileActions.map((fa: any) => {
  //         if (fa && fa.type === 'file' && fa.content && typeof fa.content !== 'string') {
  //           const { content, ...rest } = fa;
  //           return rest;
  //         }

  //         return fa;
  //       });
  //     }
  //   }

  //   console.log('[pushEventToQueueIfComplete] fileActions after upload >>>', event.fileActions);

  //   // Strip error field before sending to queue
  //   const { error, ...eventWithoutError } = event;
  //   const eventToEnqueue = eventWithoutError as DualiteEvent;

  //   const response = await fetch('/api/event/enqueue', {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //     body: JSON.stringify({ event: eventToEnqueue }),
  //   });

  //   if (response.ok) {
  //     console.log('[DualiteEvents] Event enqueued successfully:', eventId);

  //     // alert('Event enqueued successfully');

  //     // Clear error from IndexedDB if it exists (event was successfully enqueued)
  //     const currentEvent = await getEventIDB(db, eventId);

  //     if (currentEvent && currentEvent.error) {
  //       const { error: _, ...eventWithoutError } = currentEvent;

  //       console.log('[DualiteEvents] Clearing error from IndexedDB after successful enqueue:', eventWithoutError);
  //       await upsertEvent(db, {
  //         ...eventWithoutError,
  //         cacheTime: event.cacheTime,
  //         queuedAt: new Date().toISOString(),
  //         error: null,
  //         enqueued: true,
  //       });
  //       console.log('[DualiteEvents] Cleared error from IndexedDB after successful enqueue:', eventId);
  //     } else {
  //       // Update event to mark as queued (no error to clear)
  //       await upsertEvent(db, {
  //         eventId,
  //         cacheTime: event.cacheTime,
  //         queuedAt: new Date().toISOString(),
  //         enqueued: true,
  //       });
  //     }

  //     chatStore.setKey('enqueued', true);

  //     console.log('[DualiteEvents] Event deleted from IndexedDB:', eventId);

  //     // Check if connection exists, if not create one
  //     if (isConnected()) {
  //       console.log('[DualiteEvents] SSE connection is active, event will sync automatically via real-time stream');

  //       /*
  //        * Optionally set initial status in store (will be updated by SSE stream)
  //        * This provides immediate feedback while waiting for DB trigger → NOTIFY → SSE
  //        */
  //       setEventStatus(eventId, {
  //         status: 'PENDING',
  //         projectId: event.projectId,
  //         chatId: event.cloudChatId,
  //         userId: event.userId,
  //         timestamp: new Date().toISOString(),
  //       });
  //     } else {
  //       console.log('[DualiteEvents] No SSE connection found, creating new connection...');

  //       // Create connection if it doesn't exist
  //       try {
  //         await ensureEventStatusConnection(event.projectId as string, event.cloudChatId, event.userId);
  //         console.log('[DualiteEvents] SSE connection established successfully');
  //       } catch (error) {
  //         console.error('[DualiteEvents] Failed to establish SSE connection:', error);
  //         console.warn('[DualiteEvents] Event will sync when connection is established later');
  //       }
  //     }
  //   } else {
  //     const errorText = await response.text();
  //     console.error('[DualiteEvents] Failed to enqueue event:', errorText);

  //     // Create error object
  //     const errorObj: EventErrorDetails = {
  //       message: 'Failed to queue your update. Please try again.',
  //       code: `HTTP_${response.status}`,
  //       retryable: true,
  //       errorType: 'ENQUEUE_ERROR',
  //       metadata: {
  //         eventId,
  //         projectId: event.projectId,
  //         cloudChatId: event.cloudChatId,
  //         response: errorText,
  //       },
  //     };

  //     // Set error in the event status store
  //     setEventStatus(eventId, {
  //       status: 'FAILED',
  //       eventState: errorObj.errorType,
  //       projectId: event.projectId,
  //       chatId: event.cloudChatId,
  //       userId: event.userId,
  //       timestamp: new Date().toISOString(),
  //       error: errorObj,
  //     });

  //     // Persist error to IndexedDB
  //     await upsertEvent(db, {
  //       eventId,
  //       error: errorObj,
  //     });

  //     throw new Error('[pushEventToQueueIfComplete] Failed to enqueue event: ' + errorText);
  //   }
  // } catch (error) {
  //   console.error('[DualiteEvents] Error enqueueing event:', error);

  //   Sentry.captureException(error, {
  //     extra: {
  //       eventId,
  //       projectId: event?.projectId,
  //       cloudChatId: event?.cloudChatId,
  //     },
  //   });

  //   // Determine error type based on error message
  //   const errorMessage = error instanceof Error ? error.message : String(error);
  //   const isUploadError = errorMessage.includes('multipart upload') || errorMessage.includes('upload');

  //   // Create error object
  //   const errorObj: EventErrorDetails = {
  //     message: isUploadError
  //       ? 'Failed to upload files. Please try again.'
  //       : 'Failed to process your update. Please try again.',
  //     retryable: true,
  //     errorType: isUploadError ? 'UPLOAD_ERROR' : 'ENQUEUE_ERROR',
  //     metadata: {
  //       eventId,
  //       projectId: event?.projectId,
  //       cloudChatId: event?.cloudChatId,
  //       error: errorMessage,
  //     },
  //   };

  //   if (event) {
  //     // Set error in the event status store
  //     setEventStatus(eventId, {
  //       status: 'FAILED',
  //       eventState: errorObj.errorType,
  //       projectId: event.projectId,
  //       chatId: event.cloudChatId,
  //       userId: event.userId,
  //       timestamp: new Date().toISOString(),
  //       error: errorObj,
  //     });

  //     // Persist error to IndexedDB
  //     await upsertEvent(db, {
  //       eventId,
  //       error: errorObj,
  //     });
  //   }

  //   throw new Error('[pushEventToQueueIfComplete] Error enqueueing event: ' + error);
  // }
}

export async function deleteAllEvents(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('DualiteEvents', 'readwrite');
    const store = transaction.objectStore('DualiteEvents');
    store.clear();
    resolve();
  });
}

export async function deleteEvents(db: IDBDatabase, eventIds: string[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('DualiteEvents', 'readwrite');
    const store = transaction.objectStore('DualiteEvents');

    for (const eventId of eventIds) {
      store.delete(eventId);
    }

    console.log('[DualiteEvents] Completed events deleted');

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function hasUnenqueuedEventsForProject(db: IDBDatabase, projectId: string): Promise<boolean> {
  if (!db || !projectId) {
    return false;
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('DualiteEvents', 'readonly');
    const store = transaction.objectStore('DualiteEvents');
    const request = store.openCursor();
    let settled = false;

    request.onsuccess = (event) => {
      if (settled) {
        return;
      }

      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

      if (cursor) {
        const eventData = cursor.value as DualiteEvent;

        if (eventData.projectId === projectId && !eventData.enqueued) {
          settled = true;
          resolve(true);
          return;
        }

        cursor.continue();
        return;
      }

      settled = true;
      resolve(false);
    };

    request.onerror = () => {
      if (settled) {
        return;
      }

      settled = true;
      reject(request.error);
    };
  });
}

/**
 * Get all failed events from IndexedDB (events with error field) for a specific project
 */
export async function getFailedEventsFromDB(db: IDBDatabase, projectId: string): Promise<DualiteEvent[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('DualiteEvents', 'readonly');
    const store = transaction.objectStore('DualiteEvents');
    const request = store.openCursor();
    const failedEvents: DualiteEvent[] = [];

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

      if (cursor) {
        const eventData = cursor.value as DualiteEvent;

        // Check if event has an error field and matches the projectId
        if (eventData.projectId === projectId && (!eventData.enqueued || eventData.error)) {
          failedEvents.push(eventData);
        }

        cursor.continue();
      } else {
        console.log('[DualiteEvents] Loaded failed events from IndexedDB for project:', projectId, failedEvents.length);
        resolve(failedEvents);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

// cloudRevert
// type RetryEventPayload = {
//   eventId: string;
//   eventState?: EventErrorState;
// };

// export async function retryFailedEvents(
//   db: IDBDatabase,
//   eventsToRetry: RetryEventPayload[],
// ): Promise<{ success: boolean; statusCode: number }> {
//   console.log('[DualiteEvents] Retrying multiple failed events:', eventsToRetry);

//   if (eventsToRetry.length === 0) {
//     throw new Error('No event IDs provided for retry');
//   }

//   try {
//     // Get all events from IndexedDB (might be undefined for PROCESSING_ERROR events)
//     const localEvents = await Promise.all(eventsToRetry.map(({ eventId }) => getEventIDB(db, eventId)));

//     // Get error types from eventStatusStore
//     const eventStatusStoreData = eventStatusStore.get();

//     // Separate events: ENQUEUE_ERROR vs backend-handled errors
//     const enqueueErrorEvents: DualiteEvent[] = [];
//     const backendRetryEvents: Array<{ eventId: string; errorType: EventErrorState }> = [];

//     eventsToRetry.forEach(({ eventId, eventState }, index) => {
//       const eventStatus = eventStatusStoreData[eventId];
//       const resolvedErrorType =
//         eventState || (eventStatus?.error?.errorType as EventErrorState) || ('ENQUEUE_ERROR' as EventErrorState);
//       const event = localEvents[index];

//       console.log('[DualiteEvents] retryFailedEvents classification', {
//         eventId,
//         eventState,
//         fallbackErrorType: eventStatus?.error?.errorType,
//         resolvedErrorType,
//         hasLocalEvent: !!event,
//       });

//       if (resolvedErrorType === 'ENQUEUE_ERROR' || resolvedErrorType === 'UPLOAD_ERROR') {
//         if (event) {
//           enqueueErrorEvents.push(event);
//         } else {
//           console.warn(
//             '[DualiteEvents] Expected to find local event for retry but none was found. Skipping local retry.',
//             eventId,
//           );
//         }
//       } else {
//         backendRetryEvents.push({
//           eventId,
//           errorType: resolvedErrorType,
//         });
//       }
//     });

//     if (enqueueErrorEvents.length === 0 && backendRetryEvents.length === 0) {
//       throw new Error('No valid events found for retry');
//     }

//     // Handle ENQUEUE_ERROR events locally
//     if (enqueueErrorEvents.length > 0) {
//       console.log('[DualiteEvents] Retrying ENQUEUE_ERROR events locally:', enqueueErrorEvents.length);

//       for (const event of enqueueErrorEvents) {
//         /*
//          * Retry the enqueue operation
//          * Note: Don't change status to PENDING here - keep as FAILED during retry
//          * so the error card stays visible with loading state
//          */
//         try {
//           await pushEventToQueueIfComplete(db, event.eventId, 'manual', true);
//         } catch (error) {
//           Sentry.captureException(`Error from retryFailedEvents pushEventToQueueIfComplete: ${error}`, {
//             extra: {
//               eventId: event.eventId,
//               projectId: event.projectId,
//               cloudChatId: event.cloudChatId,
//             },
//           });

//           console.error('[DualiteEvents] Error from retryFailedEvents pushEventToQueueIfComplete:', error);

//           const statusCode =
//             typeof error === 'object' && error && 'statusCode' in error && typeof (error as any).statusCode === 'number'
//               ? (error as any).statusCode
//               : error instanceof Response
//                 ? error.status
//                 : 500;

//           return { success: false, statusCode };
//         }
//       }

//       console.log('[DualiteEvents] Local retry completed for ENQUEUE_ERROR events');
//     }

//     // Handle other error types via backend
//     if (backendRetryEvents.length > 0) {
//       console.log('[DualiteEvents] Sending events to backend for retry:', backendRetryEvents.length);
//       await sendEventsToBackendForRetry(backendRetryEvents);
//       console.log('[DualiteEvents] Backend retry successful for events:', backendRetryEvents);
//     }

//     console.log('[DualiteEvents] Batch retry completed successfully');
//     return { success: true, statusCode: 200 };
//   } catch (error) {
//     console.error('[DualiteEvents] Batch retry failed:', error);
//     Sentry.captureException(`Error from retryFailedEvents sendEventsToBackendForRetry: ${error}`, {
//       extra: {
//         eventsToRetry,
//       },
//     });
//     return { success: false, statusCode: 500 };
//   }
// }

// async function sendEventsToBackendForRetry(retryEvents: Array<{ eventId: string; errorType: EventErrorState }>) {
//   if (retryEvents.length === 0) {
//     return;
//   }

//   console.log('[sendEventsToBackendForRetry] Sending events to backend for retry:', retryEvents);

//   const response = await fetch('/api/event/retry-batch', {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify({ retryEvents }),
//   });

//   if (!response.ok) {
//     const errorText = await response.text();
//     throw new Error(`Failed to retry events via backend: ${errorText}`);
//   }
// }

export async function runProjectIdMigration(db: IDBDatabase, ownerId: string): Promise<void> {
  // Prevent concurrent migration runs
  if (migrationInProgress) {
    console.log('[projectId] Migration already in progress, skipping');
    return;
  }

  // Check if migration is actually needed by querying the database
  const needsMigration = await checkIfMigrationNeeded(db);

  console.log('[projectId] needsMigration >>>', needsMigration);
  needsMigrationStore.set({
    needsMigration,
    migrationFailed: false,
    migrationError: undefined,
  });

  if (!needsMigration) {
    console.log('[projectId] Migration not needed (all records have projectId or no records exist)');
    return;
  }

  console.log('[projectId] Starting deferred migration with ownerId');
  migrationInProgress = true;

  try {
    await performProjectIdMigration(db, ownerId);
    needsMigrationStore.set({
      needsMigration: false,
      migrationFailed: false,
      migrationError: undefined,
    });
    console.log('[projectId] Migration completed successfully');
  } catch (error) {
    needsMigrationStore.set({
      needsMigration: true,
      migrationFailed: true,
      migrationError: error instanceof Error ? error.message : 'Failed to migrate your projects to cloud',
    });

    Sentry.captureException(error, {
      extra: {
        message: 'Uploading projects data to cloud failed',
        ownerId,
      },
    });
  } finally {
    migrationInProgress = false;
  }
}

const isProjectIdNotUUID = (projectId: string): boolean => {
  return !/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(projectId);
};

// Helper function to check if migration is needed by examining database state
async function checkIfMigrationNeeded(db: IDBDatabase): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const getAllRequest = store.getAll();

    getAllRequest.onsuccess = () => {
      const projects = getAllRequest.result as ChatHistoryItem[];

      // If no projects exist, no migration needed
      if (projects.length === 0) {
        resolve(false);
        return;
      }

      // Check if any project is missing projectId
      const needsMigration = projects.some(
        (project) =>
          !project.hasOwnProperty('projectId') ||
          project.projectId === undefined ||
          isProjectIdNotUUID(project.projectId),
      );

      resolve(needsMigration);
    };

    getAllRequest.onerror = () => reject(getAllRequest.error);
  });
}

async function performProjectIdMigration(db: IDBDatabase, ownerId: string): Promise<void> {
  console.log('[projectId] Starting migration with ownerId:', ownerId);

  // Read all existing projects
  const projects = await new Promise<ChatHistoryItem[]>((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const getAllRequest = store.getAll();

    getAllRequest.onsuccess = () => resolve(getAllRequest.result);
    getAllRequest.onerror = () => reject(getAllRequest.error);
  });

  const projectWithoutProjectId = projects.filter(
    (project) => project.projectId === undefined || isProjectIdNotUUID(project.projectId),
  );

  console.log(`[projectId] Found ${projectWithoutProjectId.length} projects to migrate`);

  // cloudRevert
  // Prepare data for cloud upload
  // const valuesArray = projectWithoutProjectId.map((project) => {
  //   const [framework, cssLib] = project.techStack?.framework.split('_') ?? [];
  //   const techStack = {
  //     framework,
  //     cssLib,
  //     language: project.techStack?.language,
  //     packageManager: project.techStack?.packageManager,
  //   };

  //   const connectionsData: ConnectionData[] = [];

  //   if (project.connections?.github) {
  //     connectionsData.push({
  //       connectionType: 'github',
  //       connectionData: project.connections?.github,
  //     });
  //   }

  //   if (project.connections?.supabase) {
  //     connectionsData.push({
  //       connectionType: 'supabase',
  //       connectionData: project.connections?.supabase,
  //     });
  //   }

  //   if (project.netlifyCreds) {
  //     connectionsData.push({
  //       connectionType: 'netlify',
  //       connectionData: {
  //         netlifyUrl: project.netlifyCreds.netlifyUrl,
  //         netlifySiteId: project.netlifyCreds.netlifySiteId,
  //         netlifyToken: project.netlifyCreds.netlifyToken,
  //         inspectDeployment: project.netlifyCreds.inspectDeployment,
  //       },
  //     });
  //   }

  //   const newProjectId = project.projectId && isProjectIdNotUUID(project.projectId) ? undefined : project.projectId;

  //   return {
  //     ownerId,
  //     currentVersion: null,
  //     urlId: project.urlId,
  //     projectName: project.description,
  //     techStack,
  //     isGitRepo: project.isGitRepo,
  //     initSource: project.initSource,
  //     type: 'local',
  //     idbChatId: project.id,
  //     createdAt: project.timestamp,
  //     lastOpenedOn: project.timestamp,
  //     projectId: newProjectId,
  //     connectionsData,
  //     projectRules: project.projectRules,
  //   };
  // });

  // Upload to cloud and get projectIds
  // let projectIds: ProjectIdInterface[] = [];

  // try {
  // const response = await fetch('/api/projects', {
  //   method: 'POST',
  //   body: JSON.stringify({ projectData: valuesArray, type: 'upload' }),
  //   headers: {
  //     'Content-Type': 'application/json',
  //   },
  // });
  // if (!response.ok) {
  //   throw new Error();
  // }
  // const responseData = (await response.json()) as {
  //   message: ProjectIdInterface[];
  // };
  // projectIds = responseData.message;
  // console.log('[projectId] Successfully uploaded projects to cloud');
  // } catch (error) {
  //   throw new Error('Failed to migrate your projects to cloud');
  // }

  // console.log('[projectId] projectIds from the api before setting in idb >>>', projectIds);

  // Update local IndexedDB records with projectIds
  await new Promise<void>((resolve, reject) => {
    const updateTransaction = db.transaction(['chats'], 'readwrite');
    const updateStore = updateTransaction.objectStore('chats');

    projects.forEach((record) => {
      console.log('[projectId] record >>>', record.urlId);

      // cloudRevert
      // const newProjectId = projectIds.find(
      //   (p) =>
      //     (p.urlId === record.urlId && p.chatId === record.id) ||
      //     (record.urlId === undefined && p.chatId === record.id),
      // )?.projectId;

      const newProjectId = uuidv4();

      const oldProjectId = record.projectId;

      if (newProjectId) {
        record.projectId = newProjectId;

        console.log('[projectId] Updating record:', {
          id: record.id,
          urlId: record.urlId,
          projectId: newProjectId,
        });

        if (oldProjectId) {
          deleteByProjectId(db, oldProjectId);
        }
      }

      updateStore.put(record);
    });

    updateTransaction.oncomplete = () => {
      console.log('[projectId] Completed adding projectId field to existing records');
      resolve();
    };

    updateTransaction.onerror = () => {
      console.error('[projectId] Error updating records:', updateTransaction.error);
      reject(updateTransaction.error);
    };
  });

  console.log('[projectId] Migration completed with setting projectIds in idb');
}

export async function setProjectId(db: IDBDatabase, projectId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const getRequest = store.get(projectId);

    console.log('[floww] projectId from setProjectId >>>', projectId);

    getRequest.onsuccess = async () => {
      const existingChat = getRequest.result;

      if (existingChat) {
        console.log('[floww] existingChat from setProjectId >>>', existingChat);

        existingChat.projectId = projectId;

        const putRequest = store.put(existingChat);

        putRequest.onsuccess = async () => {
          // cloudRevert

          // Get the current assistant message ID (eventId) and attempt to queue the event
          // try {
          //   const eventId = chatStore.get().lastAssistantMessageId;

          //   if (eventId) {
          //     // update projectId in the event
          //     await upsertEvent(db, {
          //       eventId,
          //       projectId,
          //     });
          //     console.log('[pushing event to queue] [setProjectId]');
          //     pushEventToQueueIfComplete(db, eventId);
          //   }
          // } catch (error) {
          //   console.error('[flow] Error queuing event after projectId update:', error);

          //   Sentry.captureException(`Error from setProjectId in db.ts: ${error}`, {
          //     extra: {
          //       projectId,
          //       eventId: chatStore.get().lastAssistantMessageId,
          //     },
          //   });
          // }

          resolve();
        };
        putRequest.onerror = () => reject(putRequest.error);
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

// cloudRevert (burst)
// export async function setCloudChatId(db: IDBDatabase, projectId: string, cloudChatId: string): Promise<void> {
//   return new Promise((resolve, reject) => {
//     const transaction = db.transaction('chats', 'readwrite');
//     const store = transaction.objectStore('chats');
//     const getRequest = store.get(projectId);

//     console.log('[useChatHistory] setting cloud chat id in db >>>', cloudChatId);

//     getRequest.onsuccess = async () => {
//       const existingChat = getRequest.result;

//       if (existingChat) {
//         console.log('[useChatHistory] existingChat from setProjectId >>>', existingChat);

//         existingChat.cloudChatId = cloudChatId;

//         const putRequest = store.put(existingChat);

//         putRequest.onsuccess = async () => {

//           // Get the current assistant message ID (eventId) and attempt to queue the event
//           try {
//             const eventId = chatStore.get().lastAssistantMessageId;

//             if (eventId) {
//               // update cloudChatId in the event
//               await upsertEvent(db, {
//                 eventId,
//                 cloudChatId,
//               });
//               console.log('[pushing event to queue] [setCloudChatId]');
//               pushEventToQueueIfComplete(db, eventId);
//             }
//           } catch (error) {
//             console.error('[setCloudChatId] Error queuing event after cloudChatId update:', error);

//             Sentry.captureException(`Error from setCloudChatId in db.ts: ${error}`, {
//               extra: {
//                 projectId,
//                 cloudChatId,
//                 eventId: chatStore.get().lastAssistantMessageId,
//               },
//             });
//           }

//           resolve();
//         };
//         putRequest.onerror = () => reject(putRequest.error);
//       }
//     };
//     getRequest.onerror = () => reject(getRequest.error);
//   });
// }

export async function updatDataWithProjectId(db: IDBDatabase, newData: ChatHistoryItem[]): Promise<void> {
  console.log('[projectId] Updating data with projectId');

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');

    const requests = [];

    for (const data of newData) {
      const request = store.put(data);
      requests.push(request);
    }

    Promise.all(requests).then(() => resolve());
    Promise.all(requests).catch((error) => reject(error));
  });
}

export async function getAll(db: IDBDatabase): Promise<ChatHistoryItem[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result as ChatHistoryItem[]);
    request.onerror = () => reject(request.error);
  });
}
export async function getAlltoStoreInDB(db: IDBDatabase): Promise<ChatHistoryItem[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const index = store.index('urlId');
    const urlIds: ChatHistoryItem[] = [];

    const request = index.openCursor();

    request.onsuccess = (event: Event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

      if (cursor) {
        const value = cursor.value;

        if (value) {
          urlIds.push(value);
        }

        cursor.continue();
      } else {
        resolve(urlIds);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

// = { language: 'ts', framework: 'react' }

export async function setMessages(
  db: IDBDatabase,
  projectId: string,
  messages: UIMessage[],
  framework: string,
  language: string,
  packageManager: string,
  isGitRepo: boolean,
  initSource?: string,
  description?: string,
  connections?: Connections,
  ownerId?: string,
  cacheTime?: string,
  id?: string,
  urlId?: string,
  isCloudCall: boolean = false,

  // cloudChatId?: string,
): Promise<void> {
  console.log('[flow] setMessages called with projectId >>>', projectId);

  const timestamp = new Date().toISOString();
  const userEmail = userStore.get().email;
  let uniqueId: string | undefined = undefined;

  if (userEmail && timestamp) {
    uniqueId = await generateObfuscatedId(userEmail, timestamp);
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const techStack = { framework, language, packageManager: packageManager || 'yarn' };

    // First try to get existing chat
    const getRequest = store.get(projectId);

    getRequest.onsuccess = async () => {
      const existingChat = getRequest.result;
      const finalUniqueId = existingChat?.uniqueId || uniqueId;
      setUniqueId(finalUniqueId || '');

      // const prevMessageLength = existingChat?.messages?.length || 0;

      console.log('[flow] existingChat from setMessages >>>', existingChat);

      // Prepare the chat data
      const projectData = {
        ...existingChat,
        messages,
        id,
        urlId,
        timestamp,
        projectId,
        description: existingChat?.description || description,
        cacheTime: cacheTime || existingChat?.cacheTime || timestamp,
        techStack: existingChat?.techStack || techStack,
        isGitRepo: existingChat?.isGitRepo || isGitRepo || false,
        connections: existingChat?.connections || connections,
        initSource: existingChat?.initSource || initSource,
        uniqueId: finalUniqueId,
        type: existingChat?.type || 'local',

        // cloudRevert (burst)
        // cloudChatId,
      };

      // Put the new or updated chat data
      const putRequest = store.put(projectData);

      putRequest.onsuccess = async () => {
        console.log(`[flow] Success ${existingChat ? 'updating' : 'creating'} project:`, projectData);

        // cloudRevert

        // Create/update event with messages (use assistant message ID as eventId)
        // try {
        //   if (!isCloudCall) {
        //     const eventId = chatStore.get().lastAssistantMessageId;
        //     console.log('[setMessages] eventId >>>', eventId);

        //     // Don't trigger event if no new messages are added
        //     if (eventId && !(prevMessageLength === messages.length)) {
        //       await upsertEvent(db, {
        //         eventId,
        //         projectId: existingChat?.projectId || undefined,
        //         cloudChatId: existingChat?.cloudChatId || undefined,
        //         userId: ownerId,
        //         eventName: 'DB_UPDATE',
        //         timestamp,
        //         messages: messages.slice(prevMessageLength),
        //         startingIndex: prevMessageLength,
        //         messagesSet: true,
        //       });

        //       // Attempt to enqueue the event if complete (messages present, and fileActions if any)
        //       console.log('[pushing event to queue] [setMessages]');

        //       pushEventToQueueIfComplete(db, eventId);
        //     } else {
        //       console.log('[setMessages] Local project, skipping event creation');
        //     }
        //   }
        // } catch (error) {
        //   console.error('[flow] Error creating event:', error);
        // }

        resolve();
      };

      putRequest.onerror = (error) => {
        // console.error(`[setMessages] Error ${existingChat ? 'updating' : 'creating'} chat:`, error);
        reject(putRequest.error);
      };
    };

    getRequest.onerror = (error) => {
      // console.error('[setMessages] Error checking for existing chat:', error);
      reject(getRequest.error);
    };
  });
}

export async function updateProjectCacheTime(db: IDBDatabase, projectId: string, cacheTime?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const value = request.result;

      console.log('[updateProjectCacheTime] value from index get >>>', value);

      if (value) {
        value.cacheTime = cacheTime || new Date().toISOString();

        const updateReq = store.put(value);
        updateReq.onsuccess = () => resolve();
        updateReq.onerror = () => reject(updateReq.error);
      } else {
        // Nothing to update, just resolve
        resolve();
      }
    };

    request.onerror = () => reject(request.error);
  });
}

export async function getCacheTime(db: IDBDatabase, projectId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => resolve(request.result?.cacheTime || '');
    request.onerror = () => reject(request.error);
  });
}

export async function setProjectType(db: IDBDatabase, projectId: string, type: ProjectType): Promise<boolean> {
  console.log('[setProjectType] setProjectType called', projectId, type);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const value = request.result;

      if (value) {
        const projectData = {
          ...value,
          type,
        };

        console.log('[setProjectType] projectData >>>', projectData);

        const updateRequest = store.put(projectData);
        updateRequest.onsuccess = () => resolve(true);

        updateRequest.onerror = () => {
          console.error('[db] Error updating rules:', updateRequest.error);
          resolve(false);
        };
      } else {
        console.warn('[db] Chat not found when updating rules');
        resolve(false);
      }
    };

    request.onerror = () => {
      console.error('[db] Error getting chat for rules update:', request.error);
      resolve(false);
    };
  });
}

export async function setFileActions(
  db: IDBDatabase,
  projectId: string,
  fileActions: StoreFileAction[],
  isCloudCall: boolean = false,
  changeSource: 'manual' | 'ai' = 'ai',

  // cloudChatId?: string,
): Promise<void> {
  const time = new Date().toISOString();
  console.log('[setFileActions] setFileActions called', time, fileActions);

  if (fileActions.length === 0) {
    console.log('[setFileActions] No file actions to set');
    return;
  }

  const timestamp = new Date().toISOString();
  const uniqueId = uniqueIdStore.get().uniqueId || '';
  const processedFileActions = processFileActions(fileActions);
  const processedWithHashes: StoreFileAction[] = await Promise.all(processedFileActions.map(withContentHash));

  const transaction = db.transaction('chats', 'readwrite');
  const store = transaction.objectStore('chats');

  const getRequest = store.get(projectId);

  await new Promise<void>((resolve, reject) => {
    getRequest.onsuccess = async () => {
      const existingChat = getRequest.result;
      console.log('[flow] existingChat: ', existingChat);

      const finalUniqueId = existingChat?.uniqueId || uniqueId;
      setUniqueId(finalUniqueId || '');

      // Merge new file actions with existing ones
      const existingFileActions = existingChat?.fileActions || [];
      const mergedFileActions = await mergeFileActions(existingFileActions, processedWithHashes);

      console.log('[setFileActions] Merged file actions count:', mergedFileActions.length);

      const chatData = {
        ...existingChat,
        projectId,
        fileActions: mergedFileActions,
        timestamp,
        uniqueId: finalUniqueId,

        // cloudRevert (burst)
        // cloudChatId,
      };

      const putRequest = store.put(chatData);

      // Resolve/reject based on the transaction lifecycle to guarantee completion
      transaction.oncomplete = () => {
        console.log('[setFileActions] transaction complete');
        resolve();
      };

      transaction.onerror = () => {
        console.error('[setFileActions] transaction error', transaction.error);
        reject(transaction.error);
      };

      transaction.onabort = () => {
        console.error('[setFileActions] transaction aborted', transaction.error);
        reject(transaction.error);
      };

      // cloudRevert

      // update event with file actions
      // const eventId = chatStore.get().lastAssistantMessageId;
      // console.log('[setFileActions] eventId >>>', eventId);

      // const existingEvent = await getEventIDB(db, eventId);

      // const existingFileActionsInEvent = existingEvent?.fileActions || [];

      // // console.log('[setFileActions] existingFileActionsInEvent >>>', existingFileActionsInEvent);

      // // add only newly generated file actions to the event. Can filter out duplicate ones using hashes stored in the indexedDB chats object store.
      // const newFileActions = processedWithHashes.filter(
      //   (action) =>
      //     !existingFileActions.some(
      //       (existingAction: StoreFileAction) => existingAction.contentHash === action.contentHash,
      //     ),
      // );

      // console.log('[setFileActions] existingFileActions >>>', existingFileActions);
      // console.log('[setFileActions] newFileActions >>>', newFileActions);

      // // Start with merged event actions + newly generated full actions
      // let fileActions: Partial<StoreFileAction>[] = await mergeFileActions(
      //   existingFileActionsInEvent as StoreFileAction[],
      //   newFileActions as StoreFileAction[],
      // );
      // console.log('[setFileActions] existingFileActionsInEvent >>>', existingFileActionsInEvent);
      // console.log('[setFileActions] fileActions >>>', fileActions);

      // /*
      //  * Add minimal entries (hash-only, no content) for files present in mergedFileActions
      //  * but missing in the current event.
      //  */
      // const eventPaths = new Set(
      //   (fileActions as Partial<StoreFileAction>[]).map((a) => a?.filePath).filter((h): h is string => Boolean(h)),
      // );

      // console.log('[setFileActions] eventPaths >>>', eventPaths);

      // const minimalMissing = (mergedFileActions as Partial<StoreFileAction>[])
      //   .filter((a) => Boolean(a?.filePath) && !eventPaths.has(a!.filePath!))
      //   .map((a) => {
      //     console.log('[setFileActions] minimalMissing >>>', a.filePath);
      //     return {
      //       type: 'file' as const,
      //       filePath: (a as StoreFileAction).filePath,
      //       contentHash: a!.contentHash!,
      //       id: (a as StoreFileAction).filePath,
      //       isBinary: (a as StoreFileAction).isBinary || false,

      //       // intentionally omit content
      //     };
      //   });

      // if (minimalMissing.length > 0) {
      //   fileActions = [...fileActions, ...minimalMissing];
      // }

      // console.log('[setFileActions] fileActions in event >>>', fileActions);

      // // Filter out yarn.lock files to check if there are other meaningful changes
      // const nonYarnLockActions = newFileActions.filter(
      //   (action) => action.filePath !== 'yarn.lock' && action.filePath !== 'expo-env.d.ts',
      // );

      // if (newFileActions.length > 0 && !isCloudCall && nonYarnLockActions.length > 0) {
      //   console.log('[DualiteEvents] upserting event >>>', eventId, fileActions);

      //   console.log('[DualiteEvents] fileActions >>>', newFileActions);

      //   await upsertEvent(db, {
      //     eventId,
      //     eventName: changeSource === 'manual' ? 'MANUAL_UPDATE' : 'DB_UPDATE',
      //     fileActions,
      //     fileActionsSet: true,
      //     projectId,
      //     cloudChatId,
      //   });

      //   console.log('[DualiteEvents] [setFileActions]', isCloudCall, newFileActions.length);

      //   console.log('[pushing event to queue] [setFileActions]');
      //   pushEventToQueueIfComplete(db, eventId, changeSource);
      // } else if (newFileActions.length > 0 && nonYarnLockActions.length === 0) {
      //   console.log('[setFileActions] Skipping event upsert - only yarn.lock changes detected');
      // }

      putRequest.onsuccess = () => {
        console.log('[setFileActions] putRequest success');
      };

      putRequest.onerror = () => {
        console.error('[setFileActions] putRequest error', putRequest.error);
        reject(putRequest.error);
      };
    };

    getRequest.onerror = (error) => {
      console.error('[setFileActions] getRequest error', getRequest.error);
      reject(getRequest.error);
    };
  });
}

/**
 * Merges new file actions with existing ones.
 * - Updates existing files with new content
 * - Adds new files
 * - Removes files marked with deleted flag
 */
async function mergeFileActions(
  existingFileActions: StoreFileAction[],
  newFileActions: StoreFileAction[],
): Promise<StoreFileAction[]> {
  // Create a map of existing file actions by filePath for efficient lookup
  const fileActionsMap = new Map<string, StoreFileAction>();

  // Add all existing file actions to the map
  for (const action of existingFileActions) {
    if (action.type === 'file') {
      fileActionsMap.set(action.filePath, action);
    }
  }

  // Process new file actions
  for (const action of newFileActions) {
    if (action.type === 'file') {
      // Check for deletion flag
      if (action.deleted === true) {
        fileActionsMap.delete(action.filePath);
        console.log('[mergeFileActions] Removing deleted file:', action.filePath);
      } else {
        // Update or add the file (even if content is empty - it's a valid empty file)
        fileActionsMap.set(action.filePath, action);
        console.log('[mergeFileActions] Updating/adding file:', action.filePath);
      }
    }
  }

  // Convert map back to array
  return Array.from(fileActionsMap.values());
}

export async function getFileActions(db: IDBDatabase, projectId: string): Promise<StoreFileAction[]> {
  return new Promise((resolve, reject) => {
    if (!db || !projectId) {
      resolve([]);
      return;
    }

    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => resolve(request.result.fileActions);
    request.onerror = () => reject(request.error);
  });
}

export async function setMigratedMessages(
  db: IDBDatabase,
  id: string,
  messages: UIMessage[],
  fileActions: StoreFileAction[],
  timestamp: string,
  urlId?: string,
  description?: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');

    console.log('[refresh] files from db: ', fileActions);

    // Process fileActions to convert asset files from base64 to blobs
    const processedFileActions = processFileActions(fileActions);

    const request = store.put({
      id,
      messages,
      urlId,
      description,
      fileActions: processedFileActions,
      timestamp,
    });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function setLatestMigratedMessages(db: IDBDatabase, messageData: ChatHistoryItem): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');

    const request = store.put(messageData);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// cloudRevert (burst)
// export async function hasDataInIdb(
//   db: IDBDatabase,
//   projectId: string,
// ): Promise<{
//   hasData: boolean;
//   isOlderProject: boolean;
//   cloudChatId?: string;
// }> {
//   return new Promise((resolve, reject) => {
//     const transaction = db.transaction('chats', 'readonly');
//     const store = transaction.objectStore('chats');
//     const request = store.get(projectId);

//     request.onsuccess = () =>
//       resolve({
//         hasData: request.result !== undefined,
//         isOlderProject: request.result?.type == undefined,
//         cloudChatId: request.result?.cloudChatId,
//       });
//     request.onerror = () => reject(request.error);
//   });
// }

export async function getMessages(db: IDBDatabase, id: string): Promise<ChatHistoryItem> {
  const messages = await getMessagesByProjectId(db, id);

  return messages;
}

export async function getMessagesByProjectId(db: IDBDatabase, projectId: string): Promise<ChatHistoryItem> {
  return new Promise((resolve, reject) => {
    console.log('[useChatHistory] getting messages for projectId:', projectId);

    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => resolve(request.result as ChatHistoryItem);
    request.onerror = () => reject(request.error);
  });
}

export async function getMessagesByUrlId(db: IDBDatabase, id: string): Promise<ChatHistoryItem> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const index = store.index('urlId');
    const request = index.get(id);

    request.onsuccess = () => resolve(request.result as ChatHistoryItem);
    request.onerror = () => reject(request.error);
  });
}

export async function getMessagesById(db: IDBDatabase, projectId: string): Promise<ChatHistoryItem> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => resolve(request.result as ChatHistoryItem);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteByProjectId(db: IDBDatabase, projectId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.delete(projectId);

    request.onsuccess = () => resolve(undefined);
    request.onerror = () => reject(request.error);
  });
}

export async function getNextId(db: IDBDatabase): Promise<string> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.getAllKeys();

    request.onsuccess = () => {
      const highestId = request.result.reduce((cur, acc) => Math.max(+cur, +acc), 0);
      resolve(String(+highestId + 1));
    };

    request.onerror = () => reject(request.error);
  });
}

export async function getStoreSize(db: IDBDatabase): Promise<number> {
  return new Promise((resolve, reject) => {
    let size = 0;
    const transaction = db.transaction('chats');
    const objectStore = transaction.objectStore('chats');

    objectStore.openCursor().onsuccess = function (event) {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

      if (cursor) {
        const value = JSON.stringify(cursor.value);
        size += value.length;
        cursor.continue();
      } else {
        resolve(size); // Only resolve after all items are read
      }
    };

    objectStore.openCursor().onerror = function (event) {
      reject(event);
    };
  });
}

export async function getUrlId(db: IDBDatabase, id: string): Promise<string> {
  console.log('getUrlId called with id:', id);

  const idList = await getUrlIds(db);
  console.log('Retrieved idList:', idList);

  if (!idList.includes(id)) {
    console.log('ID not in idList, returning:', id);
    return id;
  } else {
    let i = 2;
    console.log('ID found in idList, starting increment loop');

    while (idList.includes(`${id}-${i}`)) {
      console.log(`ID ${id}-${i} already exists, incrementing i`);
      i++;
    }

    const newId = `${id}-${i}`;
    console.log('Returning new ID:', newId);
    return newId;
  }
}

async function getUrlIds(db: IDBDatabase): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const idList: string[] = [];

    const request = store.openCursor();

    request.onsuccess = (event: Event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      console.log('cursor >>>', cursor, cursor?.value);

      if (cursor) {
        idList.push(cursor.value.urlId);
        cursor.continue();
      } else {
        resolve(idList);
      }
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export const updateNetlifyUrl = async (
  db: IDBDatabase,
  projectId: string,
  deployData: {
    netlifyUrl: string;
    netlifySiteId: string;
    netlifyToken: string;
    inspectDeployment: string;
  },
) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const project = request.result;

      if (project) {
        project.netlifyCreds = deployData;

        const updateRequest = store.put(project);
        updateRequest.onsuccess = () => resolve(project);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Project not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export const updateProjectRules = async (db: IDBDatabase, projectId: string, rules: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const project = request.result;

      if (project) {
        // Update or set the projectRules field
        project.projectRules = rules;

        const updateRequest = store.put(project);
        updateRequest.onsuccess = () => resolve(true);

        updateRequest.onerror = () => {
          console.error('[db] Error updating rules:', updateRequest.error);
          resolve(false);
        };
      } else {
        console.warn('[db] Project not found when updating rules');
        resolve(false);
      }
    };

    request.onerror = () => {
      console.error('[db] Error getting project for rules update:', request.error);
      resolve(false);
    };
  });
};

export const getRules = async (db: IDBDatabase, chatId: string): Promise<string | null> => {
  return new Promise((resolve) => {
    const transaction = db.transaction(['chats'], 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;
      resolve(chat?.projectRules || null);
    };

    request.onerror = () => {
      console.error('[db] Error loading rules:', request.error);
      resolve(null); // Resolve with null instead of rejecting
    };
  });
};

export async function getFramework(
  db: IDBDatabase,
  projectId: string,
): Promise<{ framework: string; language: string } | undefined> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      console.log('request >>>', request.result);

      const project = request.result;
      resolve(project?.techStack);
    };

    request.onerror = () => reject(request.error);
  });
}

export const updateFramework = async (db: IDBDatabase, projectId: string, framework: string, language: string) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const project = request.result;

      if (project) {
        project.techStack = { language, framework };

        const updateRequest = store.put(project);
        updateRequest.onsuccess = () => resolve(project);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('project not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

// Add this interface near the top with other interfaces
export interface GitRepoStatus {
  isGitRepo: boolean;
}

// Add this function with other export functions
export async function getGitRepoStatus(projectId: string): Promise<GitRepoStatus> {
  console.log('[db] openDatabase called from getGitRepoStatus');

  const db = await openDatabase();

  if (!db) {
    throw new Error('Failed to open database');
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const chat = request.result;
      db.close();
      resolve({ isGitRepo: chat?.isGitRepo ?? false });
    };

    request.onerror = () => {
      console.error('[db] Error loading git repo status:', request.error);
      db.close();
      reject(request.error);
    };
  });
}

// = { platform: RepoDetails }
export async function getGitRepo(projectId: string): Promise<Connections | null> {
  console.log('[db] openDatabase called from getGitRepo');

  const db = await openDatabase();

  if (!db) {
    throw new Error('Failed to open database');
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const project = request.result;
      console.log('[db] getGitRepo project result:', project);

      db.close();

      if (!project) {
        console.log('[db] No project found for ID:', projectId);
        resolve(null);
        return;
      }

      if (!project.connections) {
        console.log('[db] No connections found for project:', projectId);
        resolve(null);
        return;
      }

      resolve(project.connections);
    };

    request.onerror = () => {
      console.error('[db] Error loading git repo:', request.error);
      db.close();
      reject(request.error);
    };
  });
}

export const updateGithubStatus = async (db: IDBDatabase, projectId: string, connections: Connections) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const project = request.result;
      console.log('connections 2 >>>', connections);

      if (project) {
        if (connections && Object.keys(connections).length > 0) {
          project.isGitRepo = true;
          project.connections = { ...project.connections, ...connections };
          console.log('project.connections after update >>>', project.connections);
        } else {
          project.isGitRepo = false;
          project.initSource = 'alpha';
        }

        const updateRequest = store.put(project);
        updateRequest.onsuccess = () => resolve(project);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Project not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export const updatePackageManager = async (db: IDBDatabase, projectId: string, packageManager: string) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const project = request.result;

      if (project) {
        project.techStack.packageManager = packageManager;

        const updateRequest = store.put(project);
        updateRequest.onsuccess = () => resolve(project);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Project not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export const clearGithubConnection = async (db: IDBDatabase, projectId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        // If connections exist, remove github while preserving other connections
        if (chat.connections) {
          const { github, ...restConnections } = chat.connections;
          chat.connections = restConnections;
          console.log('chat connections after removing github >>>', chat.connections);

          // If no more connections exist, set isGitRepo to false
          chat.isGitRepo = false;
        }

        console.log('chat >>>', chat.connections, chat.isGitRepo);

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export async function getUniqueId(projectId: string): Promise<string | undefined> {
  console.log('projectId from getUniqueId >>>', projectId);

  if (!projectId) {
    setUniqueId('');
    return '';
  }

  console.log('[db] openDatabase called from getUniqueId');

  const db = await openDatabase();

  if (!db) {
    throw new Error('Failed to open database');
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const chat = request.result;
      db.close();

      if (!chat?.uniqueId) {
        setUniqueId('');
        resolve('');
      } else {
        setUniqueId(chat?.uniqueId);
        resolve(chat?.uniqueId);
      }
    };

    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}

export interface SupabaseData {
  projectId: string;
  projectName: string;
  anonKey: string;
  organizationId: string;
  organizationName: string;
}

export async function getSupabaseData(db: IDBDatabase, projectId: string): Promise<SupabaseData | null> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const project = request.result;
      console.log('getSupabaseData hitting >>>', project.connections);

      resolve(project?.connections?.supabase as SupabaseData | null);
      db.close();
    };

    request.onerror = () => reject(request.error);
  });
}

export async function updateSupabaseData(
  db: IDBDatabase,
  projectId: string,
  supabaseProjectId: string,
  supabaseProjectName: string,
  supabaseAnonKey: string,
  supabaseOrganizationId: string,
  supabaseOrganizationName: string,
) {
  console.log('[supa] updateSupabase func called >>>', projectId);

  if (
    supabaseProjectId &&
    supabaseProjectName &&
    supabaseAnonKey &&
    supabaseOrganizationId &&
    supabaseOrganizationName
  ) {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['chats'], 'readwrite');
      const store = transaction.objectStore('chats');
      const request = store.get(projectId);

      request.onsuccess = () => {
        const chat = request.result;
        console.log('chat >>>', chat.connections);

        chat.connections = {
          ...chat.connections,
          supabase: {
            projectId: supabaseProjectId,
            projectName: supabaseProjectName,
            anonKey: supabaseAnonKey,
            organizationId: supabaseOrganizationId,
            organizationName: supabaseOrganizationName,
          } as SupabaseData,
        };
        console.log('chat.connections after update >>>', chat.connections);

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(chat);
        updateRequest.onerror = () => reject(updateRequest.error);
      };

      request.onerror = () => reject(request.error);
    });
  } else {
    await clearSupabaseConnection(db, projectId);
    return Promise.resolve();
  }
}

export const clearSupabaseConnection = async (db: IDBDatabase, projectId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        // If connections exist, remove supabase while preserving other connections
        if (chat.connections) {
          const { supabase, ...restConnections } = chat.connections;
          chat.connections = restConnections;
          console.log('chat connections after removing supabase >>>', chat.connections);
        }

        console.log('chat >>>', chat.connections);

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export async function setProjectDescription(db: IDBDatabase, projectId: string, description: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const chat = request.result;

      if (!chat) {
        reject(new Error('Chat not found'));
        return;
      }

      chat.description = description;

      const updateRequest = store.put(chat);
      updateRequest.onsuccess = () => resolve();
      updateRequest.onerror = () => reject(updateRequest.error);
    };

    request.onerror = () => reject(request.error);
  });
}

export async function setTechStack(
  db: IDBDatabase,
  projectId: string,
  techStack: {
    framework: string;
    language: string;
    packageManager: string;
  },
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(projectId);

    request.onsuccess = () => {
      const chat = request.result;

      if (!chat) {
        reject(new Error('Chat not found'));
        return;
      }

      chat.techStack = techStack;

      const updateRequest = store.put(chat);
      updateRequest.onsuccess = () => resolve();
      updateRequest.onerror = () => reject(updateRequest.error);
    };

    request.onerror = () => reject(request.error);
  });
}
